import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { NA } from '../constants';
import {
  getDiscoverSentimentEmoji,
  getSentimentVariantFromScore,
} from 'src/components/discover-sentiment-emoji/helpers';
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter';
import { isSentimentInsightsProcessing } from 'src/utils/solve/deflectionInsightsUtils';

type TypographyVariant = ComponentProps<typeof Typography>['variant'];

interface SentimentInsightCellProps {
  fontVariant?: TypographyVariant;
  sentimentScore: number | null | undefined;
  timestamp: string;
}

export const SentimentInsightCell = ({
  fontVariant = 'font14',
  sentimentScore,
  timestamp,
}: SentimentInsightCellProps) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  const hasSentimentScore = typeof sentimentScore === 'number';
  const isProcessing = isSentimentInsightsProcessing({
    hasSentimentScore,
    modifiedDate: timestamp,
  });

  if (!isProcessing && !hasSentimentScore) {
    return (
      <Typography color={palette.colors.grey[600]} variant={fontVariant}>
        {NA}
      </Typography>
    );
  }

  if (isProcessing) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <ProcessingBadge>
          <Typography color={palette.colors.white} noWrap variant='font11'>
            {t('sentimentInsightCell.processing')}
          </Typography>
        </ProcessingBadge>
      </Box>
    );
  }

  const sentimentVariant = getSentimentVariantFromScore(sentimentScore!);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: '4px',
      }}
    >
      <img src={getDiscoverSentimentEmoji(sentimentVariant, 'medium')} />
      <Typography variant={fontVariant}>
        {capitalizeFirstLetter(sentimentVariant)}
      </Typography>
    </Box>
  );
};

const ProcessingBadge = styled('div')`
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 4px;
  text-transform: capitalize;
  background-color: ${({ theme }) => theme.palette.colors.grey[500]};
  border-radius: 4px;
`;
